import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface TipoAgendaType {
    id: number;
    description: string;    
    empresaId: number;
}

export interface CreateTipoAgendaType {
    description: string;
    empresaId: number;
}

export interface UpdateTipoAgendaType {
    id: number;
    description: string;
}

export default function useTipoAgenda() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listTipoAgenda, setListTipoAgenda] = useState<TipoAgendaType[]>([]);
    const [agendaPageCount, setAgendaPageCount] = useState<number>(0);
    const [initialListTipoAgenda, setInitialListTipoAgenda] = useState<TipoAgendaType[]>([]);
    const [tipoAgenda, setTipoAgenda] = useState<TipoAgendaType>({
        id: 0,
        description: "",
        empresaId: 0,
    });

    function createTipoAgenda(createTipoAgendaType: CreateTipoAgendaType): Promise<CreateTipoAgendaType | null> {
        setLoading(true);
        return api
            .post(`/TipoAgenda/create`, createTipoAgendaType)
            .then((response) => {
                const createdTipoAgenda: CreateTipoAgendaType = response.data;
                showToast("success", "Tipo de agenda criada com sucesso.");
                setLoading(false);
                return createdTipoAgenda;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar tipo de agenda. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar tipo de agenda:", error);
                return null;
            });
    }

    function getAllTipoAgendaPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<TipoAgendaType[] | null> {
        setLoading(true);
        let url = `/TipoAgenda/getAllTipoAgendaPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListTipoAgenda(response.data.result);
                setInitialListTipoAgenda(response.data.result);
                setAgendaPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar tipo de agenda. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar tipo de agenda:", error);
                return null;
            });
    }

    function updateTipoAgenda(updateTipoAgendaData: UpdateTipoAgendaType): Promise<UpdateTipoAgendaType | null> {
        setLoading(true);
        return api
            .put(`/TipoAgenda/${updateTipoAgendaData.id}`, updateTipoAgendaData)
            .then((response) => {
                showToast("success", "Tipo de agenda atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar tipo de agenda. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar tipo de agenda:", error);
                return null;
            });
    }

    function deleteTipoAgenda(tipoAgendaId: number): Promise<TipoAgendaType | null> {
        setLoading(true);
        return api
            .delete(`/TipoAgenda/${tipoAgendaId}`)
            .then(() => {
                setTipoAgenda({
                    id: 0,
                    description: "",
                    empresaId: 0,
                });
                showToast("success", "Tipo de agenda deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar tipo de agenda. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar tipo de agenda:", error);
                return null;
            });
    }

    return {
        createTipoAgenda,
        getAllTipoAgendaPaged,
        updateTipoAgenda,
        deleteTipoAgenda,
        setListTipoAgenda,
        listTipoAgenda,
        setAgendaPageCount,
        agendaPageCount,
        setInitialListTipoAgenda,
        initialListTipoAgenda,
        setTipoAgenda,
        tipoAgenda,
    };
}